/**
 * Formats a fraction in 0-1 as a whole-number percentage string.
 *
 * Takes `number`, not `number | undefined`: answering `0%` for an absent value
 * renders a missing score as a real one. The 0-100 variant is `formatPercent`
 * in `pages/organization/performance/utils.ts`.
 *
 * @param value - The fraction to format (0-1 scale, e.g., 0.85 for 85%)
 * @returns Formatted percentage string (e.g., "85%")
 *
 * @example
 * formatPercentage(0.85); // "85%"
 * formatPercentage(0.123); // "12%"
 */
export function formatPercentage(value: number): string {
  return `${Math.round(value * 100)}%`;
}

/**
 * Parses a string to an integer, returning a default value if parsing fails.
 *
 * @param value - The string value to parse
 * @param defaultValue - The default value to return if parsing fails (defaults to 0)
 * @returns The parsed integer or the default value
 *
 * @example
 * parseStringToInt('123'); // 123
 * parseStringToInt(''); // 0
 * parseStringToInt(undefined); // 0
 * parseStringToInt('abc', -1); // -1
 */
export function parseStringToInt(
  value: string | undefined | null,
  defaultValue: number = 0,
): number {
  if (value === undefined || value === null || value === '') {
    return defaultValue;
  }
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
}

/**
 * Formats a number with comma separators for thousands.
 *
 * @param value - The numeric value to format
 * @returns Formatted number string with commas (e.g., "1,234")
 *
 * @example
 * formatWithCommas(1234567); // "1,234,567"
 * formatWithCommas("1234"); // "1,234"
 * formatWithCommas(undefined); // "0"
 */
export function formatWithCommas(value: string | number | undefined): string {
  if (value === undefined || value === null) {
    return '0';
  }
  const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
  if (isNaN(numValue)) {
    return '0';
  }
  return numValue.toLocaleString('en-US');
}

/** Constructing an `Intl.NumberFormat` costs far more than using one. */
const numberFormatters = new Map<number, Intl.NumberFormat>();

/**
 * Formats a number with thousands separators, keeping up to
 * `maximumFractionDigits` fraction digits.
 *
 * The digit limit is the caller's to choose because `Intl` defaults it to 3,
 * which silently rounded an automation's `0.000001` output to `0`. Callers
 * that report raw data rather than a summary pass 20, the most `Intl` accepts,
 * and so keep every digit the number actually has.
 *
 * @param value - The number to format
 * @param maximumFractionDigits - Fraction digits to keep, 0 through 20
 * @returns Grouped number string in the reader's locale
 *
 * @example
 * formatNumber(1234567.5, 20); // "1,234,567.5"
 * formatNumber(0.000001, 20); // "0.000001"
 * formatNumber(0.000001, 3); // "0"
 */
export function formatNumber(
  value: number,
  maximumFractionDigits: number,
): string {
  let formatter = numberFormatters.get(maximumFractionDigits);
  if (!formatter) {
    formatter = new Intl.NumberFormat(undefined, { maximumFractionDigits });
    numberFormatters.set(maximumFractionDigits, formatter);
  }
  return formatter.format(value);
}

/**
 * Formats a numeric amount in its currency, in the reader's own locale.
 *
 * An unrecognised currency code makes `Intl` throw, so the fallback is a plain
 * grouped number: better a figure with no symbol than no figure at all.
 *
 * @param amount - The numeric amount to format
 * @param currency - ISO 4217 currency code, or null to default to USD
 * @returns Formatted currency string, or a grouped number if the code is invalid
 *
 * @example
 * formatAmount(1234.5, 'USD'); // "$1,234.50"
 * formatAmount(1000, null); // "$1,000.00"
 * formatAmount(1000, 'US'); // "1,000" — 'US' is not a currency code
 */
export function formatAmount(amount: number, currency: string | null): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency ?? 'USD',
    }).format(amount);
  } catch {
    return new Intl.NumberFormat().format(amount);
  }
}

/**
 * Whether a display string reads as a single number.
 *
 * Tolerates the decoration a number picks up on its way to a cell: a currency
 * symbol, thousands separators, a trailing percent sign, and the accounting
 * convention of wrapping a negative in parentheses. A blank string is not a
 * number, and neither is anything with other text in it — `2026-01-01` and
 * `INV-1` both read as words, not quantities.
 *
 * @param value - The display string to test
 * @returns True when the string is a number
 *
 * @example
 * isNumericValue('1,234.50'); // true
 * isNumericValue('$1,234'); // true
 * isNumericValue('(42)'); // true — accounting negative
 * isNumericValue('12%'); // true
 * isNumericValue('2026-01-01'); // false
 */
export function isNumericValue(value: string): boolean {
  let text = value.trim();
  if (!text) {
    return false;
  }
  if (/^\(.+\)$/.test(text)) {
    text = `-${text.slice(1, -1).trim()}`;
  }
  text = text
    .replace(/[$€£¥₹]/g, '')
    .replace(/%$/, '')
    .trim();
  return /^[-+]?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?$/.test(text);
}

/**
 * Whether a column holds quantities, judged from its values rather than its
 * name. A column of numbers is right-aligned so the digits line up; a column
 * that merely happens to be called "total" is not.
 *
 * Blank cells are ignored, since a partly filled column of figures is still a
 * column of figures. A column with nothing in it is not numeric.
 *
 * @param values - Every cell value in the column, as displayed
 * @returns True when at least one value is present and all present values are numbers
 *
 * @example
 * isNumericColumn(['1', '2.5', '']); // true
 * isNumericColumn(['1', 'n/a']); // false
 * isNumericColumn(['', '']); // false
 */
export function isNumericColumn(values: readonly string[]): boolean {
  let seen = false;
  for (const value of values) {
    if (value.trim() === '') {
      continue;
    }
    if (!isNumericValue(value)) {
      return false;
    }
    seen = true;
  }
  return seen;
}
